import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';

import { PostText } from 'components/PostText/PostText';
import { handleRedirect, setSearchParams } from 'store/search/actions';
import { routesPath } from 'utils/constants/routePath';
import { getUserByName } from 'services';
import {
  LocationIcon,
  MailIcon,
  PhoneIcon,
  WorldIcon,
} from 'public/assets/icons';
import CollaboratorsSlider from 'containers/CollaboratorsSlider';
import Pills from 'components/common/Pills';
import { IProfileDetailsDescription } from './IProfileDetailsDescription';

import {
  BottomLine,
  ContactInfoBlock,
  ContactInfoItemText,
  ContactInfoItemWrap,
  ContentInfoItemIconWrap,
  GeneralInfo,
  GeneralInfoDescription,
  HeaderText,
  LinkWrap,
  MyBrands,
  MyCollaborators,
  MyInterestBlock,
  PillWrap,
  PillWrapBrands,
  ProfileDetailsDescriptionWrapper,
} from './ProfileDetailsDescription.styles';

const ProfileDetailsDescription = ({ data }: IProfileDetailsDescription) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const theme: any = useTheme();

  const isContactInfoExists = useCallback(
    () =>
      Boolean(
        !!data?.email?.trim() ||
          !!data?.business?.email?.trim() ||
          !!data?.phone?.trim() ||
          !!data?.business?.phone?.trim() ||
          !!data.address?.trim() ||
          !!data?.website?.trim() ||
          !!data.location?.address?.trim()
      ),
    [data]
  );

  const onHashtagClick = (item) => {
    const eventTag = _.trim(item).substring(1);
    dispatch(handleRedirect(false));
    dispatch(
      setSearchParams({
        category: 4,
        start: 0,
        limit: 20,
        q: eventTag,
        paramsForFilter: 0,
      })
    );

    router.push(routesPath.searchPath);
  };

  const onMentionClick = async (item) => {
    const mentionUsername = _.trim(item).substring(1);

    const userInfo = await getUserByName(mentionUsername);

    // eslint-disable-next-line no-unused-expressions
    userInfo.status === 200 &&
      router.push(`${routesPath.user_details}/${userInfo.data._id}`);
  };

  const getWebsiteLink = (website) => {
    const protocols = ['https', 'http', 'www'];

    for (let i = 0; i < protocols.length; i += 1) {
      if (website.includes(protocols[i])) {
        return website;
      }
    }

    return `https://${website}`;
  };

  const getHeaderText = (text: string) => {
    if (data.isMe) {
      return `${t('common:my')} ${text}`;
    }

    return `${t('common:event')} ${text}`;
  };

  return (
    <ProfileDetailsDescriptionWrapper>
      {data?.description && (
        <GeneralInfo pb={16}>
          <HeaderText type="h3" mb={6}>
            {t('common:generalInfo')}
          </HeaderText>
          <GeneralInfoDescription>
            <PostText
              text={data?.description || ''}
              onHashtagClick={onHashtagClick}
              onMentionClick={onMentionClick}
            />
          </GeneralInfoDescription>
        </GeneralInfo>
      )}

      <ContactInfoBlock isContactInfoExists={isContactInfoExists()}>
        {data?.website && data?.website !== ' ' && (
          <ContactInfoItemWrap>
            <ContentInfoItemIconWrap>
              <WorldIcon />
            </ContentInfoItemIconWrap>
            <LinkWrap path={getWebsiteLink(data?.website)} target="_blank">
              <ContactInfoItemText>{data?.website}</ContactInfoItemText>
            </LinkWrap>
          </ContactInfoItemWrap>
        )}
        {data.location?.address && (
          <ContactInfoItemWrap>
            <ContentInfoItemIconWrap>
              <LocationIcon />
            </ContentInfoItemIconWrap>
            <ContactInfoItemText>{data.location?.address}</ContactInfoItemText>
          </ContactInfoItemWrap>
        )}
        {data?.business?.phone && data?.business.phone.trim() && (
          <ContactInfoItemWrap>
            <ContentInfoItemIconWrap>
              <PhoneIcon />
            </ContentInfoItemIconWrap>
            <ContactInfoItemText>{data?.business?.phone}</ContactInfoItemText>
          </ContactInfoItemWrap>
        )}
        {data?.business?.email && data?.business.email.trim() && (
          <ContactInfoItemWrap>
            <ContentInfoItemIconWrap>
              <MailIcon />
            </ContentInfoItemIconWrap>
            <ContactInfoItemText>{data?.business?.email}</ContactInfoItemText>
          </ContactInfoItemWrap>
        )}
        {isContactInfoExists() && <BottomLine />}
      </ContactInfoBlock>
      {data?.interests?.length > 0 && (
        <MyInterestBlock>
          <HeaderText>{`${data?.username} interests`}</HeaderText>
          <PillWrap>
            {data?.interests?.length > 0 &&
              data?.interests?.map((el) => (
                <Pills
                  key={el?._id}
                  type="selectTags"
                  bgColor={theme?.colors?.neutral?.n0}
                >
                  {el?.name}
                </Pills>
              ))}
          </PillWrap>
          <BottomLine pt={8} />
        </MyInterestBlock>
      )}
      {data?.brands?.length > 0 && (
        <MyBrands>
          <HeaderText type="h3">{`${data?.username} brands`}</HeaderText>
          <PillWrapBrands>
            {data?.brands?.length > 0 &&
              data?.brands.map((el) => (
                <Pills
                  key={el?._id}
                  type="selectTags"
                  bgColor={theme?.colors?.neutral?.n0}
                >
                  {el?.brand}
                </Pills>
              ))}
          </PillWrapBrands>

          {!!data?.collaborators?.length && <BottomLine />}
        </MyBrands>
      )}
      {!!data?.collaborators?.length && (
        <MyCollaborators>
          <HeaderText type="h3" mb={8}>
            {getHeaderText('collaborators')}
          </HeaderText>
          <CollaboratorsSlider collaborators={data?.collaborators} />
        </MyCollaborators>
      )}
    </ProfileDetailsDescriptionWrapper>
  );
};

export default ProfileDetailsDescription;
